import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';

interface EventDetail {
  id: number;
  title: string;
  body: string;
  event_date: string;
  event_time: string | null;
  event_incharge: string | null;
  posted_by: string;
  is_holiday: boolean; // ← added
  created_at: string;
  updated_at: string;
}

interface ViewEventModalProps {
  open: boolean;
  onClose: () => void;
  eventId: number | null;
}

const InfoRow: React.FC<{ icon: string; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', gap: '12px',
    padding: '12px 0', borderBottom: '1px solid rgba(229,231,235,0.5)',
  }}>
    <div style={{
      width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0,
      background: 'linear-gradient(135deg, rgba(30,58,138,0.08) 0%, rgba(59,130,246,0.06) 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <i className={icon} style={{ fontSize: '13px', color: '#3b82f6' }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>
        {label}
      </div>
      <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: 500, lineHeight: 1.5 }}>
        {value}
      </div>
    </div>
  </div>
);

const ViewEventModal: React.FC<ViewEventModalProps> = ({ open, onClose, eventId }) => {
  const toast = useRef<Toast>(null);
  const [event,     setEvent]     = useState<EventDetail | null>(null);
  const [fetching,  setFetching]  = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (open && eventId) fetchEventDetail();
    else if (!open) setEvent(null);
  }, [open, eventId]);

  useEffect(() => {
    if (isClosing) {
      document.querySelector('.p-dialog')?.classList.add('dialog-closing');
      document.querySelector('.p-dialog-mask')?.classList.add('mask-closing');
    }
  }, [isClosing]);

  const fetchEventDetail = async () => {
    setFetching(true);
    try {
      const response = await apiClient.get(`/events?event_id=${eventId}`);
      if (response.data.status === 'success') {
        setEvent(response.data.event);
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load event', life: 4000 });
        handleClose();
      }
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load event', life: 4000 });
      handleClose();
    } finally {
      setFetching(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { onClose(); setIsClosing(false); }, 400);
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const formatTime = (t: string) =>
    new Date(`1970-01-01T${t}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
      <div style={{
        width: '54px', height: '54px', borderRadius: '14px',
        background: event?.is_holiday
          ? 'linear-gradient(135deg, #92400e 0%, #b45309 55%, #d97706 100%)' // ← amber when holiday
          : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontSize: '24px',
        boxShadow: event?.is_holiday
          ? '0 8px 24px rgba(180,83,9,0.3)'
          : '0 8px 24px rgba(59,130,246,0.3)',
        flexShrink: 0, border: '3px solid white',
        transition: 'all 0.2s',
      }}>
        <i className={event?.is_holiday ? 'pi pi-sun' : 'pi pi-calendar'} />
      </div>
      <div>
        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
          Event Details
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
          Read-only view of this event
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '620px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)', overflowY: 'auto',
          border: '12px solid rgba(255,255,255,0.8)',
          borderRadius: '0 0 24px 24px', borderTop: 'none',
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px', borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255,255,255,0.8)',
          borderRight: '12px solid rgba(255,255,255,0.8)',
          borderTop: '12px solid rgba(255,255,255,0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; animation: maskFadeIn 0.35s ease; }
          .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
          @keyframes maskFadeIn { from { opacity:0; } to { opacity:1; } }
          @keyframes maskFadeOut { from { opacity:1; } to { opacity:0; } }
          .p-dialog { animation: dialogOpen 0.35s cubic-bezier(0.4,0,0.2,1); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25) !important; }
          .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4,0,0.2,1) !important; }
          @keyframes dialogOpen { from { opacity:0; transform:scale(0.92) translateY(-20px); } to { opacity:1; transform:scale(1) translateY(0); } }
          @keyframes dialogClose { from { opacity:1; transform:scale(1) translateY(0); } to { opacity:0; transform:scale(0.88) translateY(25px); } }
        `}</style>

        {fetching ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '16px' }}>
            <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 600 }}>Loading event details...</span>
          </div>
        ) : event ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Title banner — amber tint when holiday */}
            <div style={{
              background: event.is_holiday
                ? 'linear-gradient(135deg, #92400e 0%, #b45309 55%, #d97706 100%)'
                : 'linear-gradient(135deg, #1e3a8a 0%, #2855ae 55%, #3b82f6 100%)',
              borderRadius: '14px', padding: '20px 22px',
              boxShadow: event.is_holiday
                ? '0 6px 20px rgba(180,83,9,0.25)'
                : '0 6px 20px rgba(30,58,138,0.25)',
            }}>
              {event.is_holiday && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: 11, fontWeight: 700, color: '#fde68a',
                  background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)',
                  padding: '2px 8px', borderRadius: 5, marginBottom: 8,
                }}>
                  🏖 Holiday
                </div>
              )}
              <div style={{ fontSize: '18px', fontWeight: 700, color: 'white', marginBottom: '6px', lineHeight: 1.3 }}>
                {event.title}
              </div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.72)', lineHeight: 1.6 }}>
                {event.body}
              </div>
            </div>

            {/* Detail rows */}
            <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: '16px', padding: '8px 16px', border: '1px solid rgba(59,130,246,0.1)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
              <InfoRow
                icon="pi pi-calendar"
                label="Event Date"
                value={formatDate(event.event_date)}
              />
              <InfoRow
                icon="pi pi-clock"
                label="Event Time"
                value={event.event_time ? formatTime(event.event_time) : <span style={{ color: '#d1d5db', fontStyle: 'italic' }}>Not specified</span>}
              />
              {/* ── Holiday row ── */}
              <InfoRow
                icon="pi pi-sun"
                label="Holiday"
                value={
                  event.is_holiday
                    ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 12, fontWeight: 700,
                        color: '#b45309', background: 'rgba(251,191,36,0.12)',
                        border: '1px solid rgba(251,191,36,0.35)',
                        padding: '3px 10px', borderRadius: 6,
                      }}>🏖 Yes — marked as holiday</span>
                    )
                    : <span style={{ color: '#d1d5db', fontStyle: 'italic' }}>No</span>
                }
              />
              <InfoRow
                icon="pi pi-user"
                label="Event Incharge"
                value={event.event_incharge ?? <span style={{ color: '#d1d5db', fontStyle: 'italic' }}>Not assigned</span>}
              />
              <InfoRow
                icon="pi pi-send"
                label="Posted By"
                value={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '10px', fontWeight: 700, color: 'white', flexShrink: 0,
                    }}>
                      {event.posted_by.charAt(0).toUpperCase()}
                    </div>
                    {event.posted_by}
                  </div>
                }
              />
              <InfoRow
                icon="pi pi-history"
                label="Created At"
                value={new Date(event.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              />
              <div style={{ paddingTop: '12px' }}>
                <InfoRow
                  icon="pi pi-refresh"
                  label="Last Updated"
                  value={new Date(event.updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                />
              </div>
            </div>

          </div>
        ) : null}
      </Dialog>
    </>
  );
};

export default ViewEventModal;

// import React, { useState, useEffect, useRef } from 'react';
// import { Dialog } from 'primereact/dialog';
// import { Toast } from 'primereact/toast';
// import { ProgressSpinner } from 'primereact/progressspinner';
// import apiClient from '../../../stateManagers/services/axiosInterceptor';

// interface EventDetail {
//   id: number;
//   title: string;
//   body: string;
//   event_date: string;
//   event_time: string | null;
//   event_incharge: string | null;
//   posted_by: string;
//   created_at: string;
//   updated_at: string;
// }

// interface ViewEventModalProps {
//   open: boolean;
//   onClose: () => void;
//   eventId: number | null;
// }

// const InfoRow: React.FC<{ icon: string; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
//   <div style={{
//     display: 'flex', alignItems: 'flex-start', gap: '12px',
//     padding: '12px 0', borderBottom: '1px solid rgba(229,231,235,0.5)',
//   }}>
//     <div style={{
//       width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0,
//       background: 'linear-gradient(135deg, rgba(30,58,138,0.08) 0%, rgba(59,130,246,0.06) 100%)',
//       display: 'flex', alignItems: 'center', justifyContent: 'center',
//     }}>
//       <i className={icon} style={{ fontSize: '13px', color: '#3b82f6' }} />
//     </div>
//     <div style={{ flex: 1, minWidth: 0 }}>
//       <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>
//         {label}
//       </div>
//       <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: 500, lineHeight: 1.5 }}>
//         {value}
//       </div>
//     </div>
//   </div>
// );

// const ViewEventModal: React.FC<ViewEventModalProps> = ({ open, onClose, eventId }) => {
//   const toast = useRef<Toast>(null);
//   const [event, setEvent]       = useState<EventDetail | null>(null);
//   const [fetching, setFetching] = useState(false);
//   const [isClosing, setIsClosing] = useState(false);

//   useEffect(() => {
//     if (open && eventId) fetchEventDetail();
//     else if (!open) setEvent(null);
//   }, [open, eventId]);

//   useEffect(() => {
//     if (isClosing) {
//       document.querySelector('.p-dialog')?.classList.add('dialog-closing');
//       document.querySelector('.p-dialog-mask')?.classList.add('mask-closing');
//     }
//   }, [isClosing]);

//   const fetchEventDetail = async () => {
//     setFetching(true);
//     try {
//       const response = await apiClient.get(`/events?event_id=${eventId}`);
//       if (response.data.status === 'success') {
//         setEvent(response.data.event);
//       } else {
//         toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load event', life: 4000 });
//         handleClose();
//       }
//     } catch {
//       toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load event', life: 4000 });
//       handleClose();
//     } finally {
//       setFetching(false);
//     }
//   };

//   const handleClose = () => {
//     setIsClosing(true);
//     setTimeout(() => { onClose(); setIsClosing(false); }, 400);
//   };

//   const formatDate = (d: string) =>
//     new Date(d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

//   const formatTime = (t: string) =>
//     new Date(`1970-01-01T${t}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

//   const headerTemplate = () => (
//     <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
//       <div style={{
//         width: '54px', height: '54px', borderRadius: '14px',
//         background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
//         display: 'flex', alignItems: 'center', justifyContent: 'center',
//         color: 'white', fontSize: '24px',
//         boxShadow: '0 8px 24px rgba(59,130,246,0.3)', flexShrink: 0, border: '3px solid white',
//       }}>
//         <i className="pi pi-calendar" />
//       </div>
//       <div>
//         <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
//           Event Details
//         </div>
//         <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
//           Read-only view of this event
//         </div>
//       </div>
//     </div>
//   );

//   return (
//     <>
//       <Toast ref={toast} position="top-right" />
//       <Dialog
//         visible={open && !isClosing}
//         onHide={handleClose}
//         header={headerTemplate}
//         style={{ width: '90vw', maxWidth: '620px', borderRadius: '24px', overflow: 'hidden' }}
//         contentStyle={{
//           padding: '18px',
//           background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
//           maxHeight: 'calc(90vh - 120px)', overflowY: 'auto',
//           border: '12px solid rgba(255,255,255,0.8)',
//           borderRadius: '0 0 24px 24px', borderTop: 'none',
//         }}
//         headerStyle={{
//           background: 'white', borderBottom: '1px solid #e5e7eb',
//           padding: '20px 24px', borderRadius: '24px 24px 0 0',
//           borderLeft: '12px solid rgba(255,255,255,0.8)',
//           borderRight: '12px solid rgba(255,255,255,0.8)',
//           borderTop: '12px solid rgba(255,255,255,0.8)',
//           margin: '-12px -12px 0 -12px',
//         }}
//         modal draggable={false} resizable={false} blockScroll
//       >
//         <style>{`
//           .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; animation: maskFadeIn 0.35s ease; }
//           .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
//           @keyframes maskFadeIn { from { opacity:0; } to { opacity:1; } }
//           @keyframes maskFadeOut { from { opacity:1; } to { opacity:0; } }
//           .p-dialog { animation: dialogOpen 0.35s cubic-bezier(0.4,0,0.2,1); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25) !important; }
//           .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4,0,0.2,1) !important; }
//           @keyframes dialogOpen { from { opacity:0; transform:scale(0.92) translateY(-20px); } to { opacity:1; transform:scale(1) translateY(0); } }
//           @keyframes dialogClose { from { opacity:1; transform:scale(1) translateY(0); } to { opacity:0; transform:scale(0.88) translateY(25px); } }
//         `}</style>

//         {fetching ? (
//           <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '16px' }}>
//             <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
//             <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 600 }}>Loading event details...</span>
//           </div>
//         ) : event ? (
//           <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

//             {/* Title banner */}
//             <div style={{
//               background: 'linear-gradient(135deg, #1e3a8a 0%, #2855ae 55%, #3b82f6 100%)',
//               borderRadius: '14px', padding: '20px 22px',
//               boxShadow: '0 6px 20px rgba(30,58,138,0.25)',
//             }}>
//               <div style={{ fontSize: '18px', fontWeight: 700, color: 'white', marginBottom: '6px', lineHeight: 1.3 }}>
//                 {event.title}
//               </div>
//               <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.72)', lineHeight: 1.6 }}>
//                 {event.body}
//               </div>
//             </div>

//             {/* Detail rows */}
//             <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: '16px', padding: '8px 16px', border: '1px solid rgba(59,130,246,0.1)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
//               <InfoRow
//                 icon="pi pi-calendar"
//                 label="Event Date"
//                 value={formatDate(event.event_date)}
//               />
//               <InfoRow
//                 icon="pi pi-clock"
//                 label="Event Time"
//                 value={event.event_time ? formatTime(event.event_time) : <span style={{ color: '#d1d5db', fontStyle: 'italic' }}>Not specified</span>}
//               />
//               <InfoRow
//                 icon="pi pi-user"
//                 label="Event Incharge"
//                 value={event.event_incharge ?? <span style={{ color: '#d1d5db', fontStyle: 'italic' }}>Not assigned</span>}
//               />
//               <InfoRow
//                 icon="pi pi-send"
//                 label="Posted By"
//                 value={
//                   <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
//                     <div style={{
//                       width: '24px', height: '24px', borderRadius: '50%',
//                       background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
//                       display: 'flex', alignItems: 'center', justifyContent: 'center',
//                       fontSize: '10px', fontWeight: 700, color: 'white', flexShrink: 0,
//                     }}>
//                       {event.posted_by.charAt(0).toUpperCase()}
//                     </div>
//                     {event.posted_by}
//                   </div>
//                 }
//               />
//               <InfoRow
//                 icon="pi pi-history"
//                 label="Created At"
//                 value={new Date(event.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
//               />
//               <div style={{ paddingTop: '12px' }}>
//                 <InfoRow
//                   icon="pi pi-refresh"
//                   label="Last Updated"
//                   value={new Date(event.updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
//                 />
//               </div>
//             </div>

//           </div>
//         ) : null}
//       </Dialog>
//     </>
//   );
// };

// export default ViewEventModal;
